import { permissionsByEntity, useCheckPermissions } from '@cmg/auth';
import type { apiTypes } from '@cmg/common';
import { Panel, PrimaryButton, SuccessButton } from '@cmg/common';
import { Stack } from '@cmg/design-system';
import { identitySelectors } from '@cmg/e2e-selectors';
import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { useDebouncedCallback } from 'use-debounce';

import type { FirmExportFilters, FirmListFilters } from '../../../common/api/rolodexApiClient';
import routeFactory from '../../../common/util/routeFactory';
import {
  exportFirms,
  fetchFirmList,
  fetchMetadata,
  selectExportFirmsLoading,
  selectFirms,
  selectFirmsError,
  selectFirmsLoading,
  selectFirmsPagination,
  selectMetadata,
  selectMetadataLoading,
} from '../shared/ducks';
import FirmList from './components/FirmList';

const mapStateToProps = state => ({
  loading: selectFirmsLoading(state),
  exportLoading: selectExportFirmsLoading(state),
  firms: selectFirms(state),
  pagination: selectFirmsPagination(state),
  error: selectFirmsError(state),
  metadata: selectMetadata(state),
  metadataLoading: selectMetadataLoading(state),
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      fetchFirmList,
      fetchMetadata,
      exportFirms,
    },
    dispatch
  ),
});

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = StateProps & DispatchProps;

export const RolodexFirmListRouteComponent: React.FC<Props> = ({
  loading,
  exportLoading,
  firms,
  pagination,
  error,
  metadata,
  metadataLoading,
  actions,
}) => {
  const [filters, setFilters] = React.useState<FirmListFilters>({});
  const canEdit = useCheckPermissions([permissionsByEntity.Firm.FULL]);
  const canExport = useCheckPermissions([permissionsByEntity.Firm.READ]);

  const debouncedFetch = useDebouncedCallback(actions.fetchFirmList, 300);

  const handleExport = React.useCallback(() => {
    const exportParams: FirmExportFilters = {
      name: filters.searchText,
      id: filters.key,
      legalEntityType: filters.legalEntityType,
      industryType: filters.industryType,
      status: filters.status,
      roleType: filters.roleType,
      deletionStatus: filters.deletionStatus,
      firmType: filters.firmType,
    };

    actions.exportFirms(exportParams);
  }, [filters, actions]);

  React.useEffect(() => {
    actions.fetchFirmList({ page: 1, perPage: 25 });
    actions.fetchMetadata();
  }, [actions]);

  const handleChangePage = (params: apiTypes.ListParams) => {
    actions.fetchFirmList({
      ...filters,
      ...params,
    });
  };

  const handleChangeFilter = (filters: FirmListFilters, debounce?: boolean) => {
    if (debounce) {
      debouncedFetch({ page: 1, ...filters });
    } else {
      actions.fetchFirmList({ page: 1, ...filters });
    }
    setFilters(filters);
  };

  return (
    <Panel testId={identitySelectors.rolodex.firmListScreen.testId}>
      <Panel.Header
        title="Organizations"
        rightContent={
          (canExport || canEdit) && (
            <Stack gap={1} padding={1} direction="row">
              {canExport && (
                <PrimaryButton
                  onClick={handleExport}
                  loading={exportLoading}
                  testId={identitySelectors.rolodex.firmListExportFirms.testId}
                >
                  Export
                </PrimaryButton>
              )}
              {canEdit && (
                <SuccessButton
                  asComponent={Link}
                  to={routeFactory.rolodexFirmImport.getUrlPath()}
                  testId={identitySelectors.rolodex.firmListCreateFirm.testId}
                >
                  Create Organization
                </SuccessButton>
              )}
            </Stack>
          )
        }
        testId={identitySelectors.rolodex.firmListHeader.testId}
      />
      <FirmList
        loading={loading || metadataLoading}
        firms={!loading && !metadataLoading ? firms : []}
        pagination={pagination}
        filters={filters}
        error={!!error}
        metadata={metadata}
        onChangeFilters={handleChangeFilter}
        onChangePage={handleChangePage}
      />
    </Panel>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(RolodexFirmListRouteComponent);
